import React, { useCallback, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

async function parseApiError(res) {
  try {
    const data = await res.json();
    return data?.error || data?.message || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function normalizePhone(value) {
  return String(value || '').replace(/\D/g, '');
}

export default function AdminSessionPanel({ authToken }) {
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState(null);
  const [rowError, setRowError] = useState('');
  const [rowErrorSessionId, setRowErrorSessionId] = useState('');
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const loadSessions = useCallback(async (resolvedUserId) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/users/${resolvedUserId}/sessions`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(await parseApiError(res));
    const data = await res.json();
    return data.sessions || [];
  }, [authToken]);

  const resolveUserId = async (rawQuery) => {
    const trimmed = rawQuery.trim();
    if (!trimmed) return null;
    if (isUuid(trimmed)) return trimmed;

    const phoneDigits = normalizePhone(trimmed);
    const res = await fetch(
      `${API_BASE_URL}/api/admin/users/lookup?phone=${encodeURIComponent(phoneDigits)}`,
      { headers: { Authorization: `Bearer ${authToken}` } },
    );

    if (res.status === 404) {
      throw new Error('No user found for that phone number');
    }
    if (res.status === 409) {
      throw new Error('Multiple users found — search by UUID instead');
    }
    if (!res.ok) {
      throw new Error('Lookup failed, try again');
    }

    const data = await res.json();
    return data?.user?.id || null;
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSessions([]);
    setUserId('');
    setSearched(false);

    try {
      const resolvedUserId = await resolveUserId(query);
      if (!resolvedUserId) {
        setError('No user found for that ID or phone number');
        setSearched(true);
        return;
      }

      const rows = await loadSessions(resolvedUserId);
      setUserId(resolvedUserId);
      setSessions(rows);
      setSearched(true);
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogoutAll = async () => {
    if (!userId) return;
    setBusy(true);
    setError('');
    setRowError('');
    setRowErrorSessionId('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/sessions/revoke-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const rows = await loadSessions(userId);
      setSessions(rows);
    } catch (err) {
      setError(err.message || 'Force logout failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!userId || !sessionId) return;
    setRevokingSessionId(sessionId);
    setRowError('');
    setRowErrorSessionId('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/sessions/${sessionId}/revoke`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({}),
        },
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      const rows = await loadSessions(userId);
      setSessions(rows);
      setRowErrorSessionId('');
    } catch (err) {
      setRowError(err.message || 'Revoke failed');
      setRowErrorSessionId(sessionId);
    } finally {
      setRevokingSessionId(null);
    }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-xs text-muted-foreground">
          User ID or phone number
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="UUID or mobile number"
            className="rounded-md border border-amber-500/20 bg-black/20 px-3 py-2 text-sm text-amber-100"
          />
        </label>
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {loading && (
        <p className="text-sm text-muted-foreground">Loading sessions…</p>
      )}

      {!loading && searched && sessions.length === 0 && !error && (
        <p className="text-sm text-muted-foreground">No active sessions for this user</p>
      )}

      {!loading && sessions.length > 0 && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-md bg-red-600/90 px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
              disabled={busy || revokingSessionId}
              onClick={handleForceLogoutAll}
            >
              {busy ? 'Revoking…' : 'Force Logout All'}
            </button>
          </div>
        <div className="overflow-x-auto rounded-lg border border-amber-500/20">
          <table className="w-full min-w-[900px] border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-amber-500/20 bg-amber-500/5 text-amber-200/80">
                <th className="px-3 py-2 font-semibold">Role</th>
                <th className="px-3 py-2 font-semibold">Device Info</th>
                <th className="px-3 py-2 font-semibold">IP Address</th>
                <th className="px-3 py-2 font-semibold">User Agent</th>
                <th className="px-3 py-2 font-semibold">Created At</th>
                <th className="px-3 py-2 font-semibold">Last Active</th>
                <th className="px-3 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((row) => (
                <tr key={row.id} className="border-b border-white/5 text-muted-foreground">
                  <td className="px-3 py-2 capitalize">{row.role || '—'}</td>
                  <td className="px-3 py-2 max-w-[120px] truncate" title={row.device_info || ''}>{row.device_info || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.ip_address || '—'}</td>
                  <td className="px-3 py-2 max-w-[180px] truncate" title={row.user_agent || ''}>{row.user_agent || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(row.last_active_at)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="rounded-md border border-amber-500/40 px-3 py-1 text-xs font-bold text-amber-200 hover:bg-amber-500/10 disabled:opacity-50"
                      disabled={busy || revokingSessionId === row.id}
                      onClick={() => handleRevokeSession(row.id)}
                    >
                      {revokingSessionId === row.id ? 'Revoking…' : 'Revoke'}
                    </button>
                    {rowError && rowErrorSessionId === row.id && (
                      <p className="mt-1 text-[10px] text-red-400">{rowError}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}
    </div>
  );
}
